// 26 ADM-004, ADM-010: every console action is a server command. The console calls
// ops.console_command(), which takes the admin from the signed-in session, checks the step-up
// (ADM-003) and runs ops.run_admin_command(): one audit row each. Hiding a button is never the
// protection; the server refuses what a role cannot do.
import type { SupabaseClient } from '@supabase/supabase-js';

export interface CommandResult {
  ok: boolean;
  error?: string;
  http_status?: number;
  [field: string]: unknown;
}

export async function runCommand(
  sb: SupabaseClient,
  command: string,
  args: Record<string, unknown>,
  reason: string,
  idempotencyKey: string = crypto.randomUUID(),
): Promise<CommandResult> {
  const { data, error } = await sb.schema('ops').rpc('console_command', {
    p_command: command,
    p_idempotency_key: idempotencyKey,
    p_args: args,
    p_reason: reason,
  });
  if (error || !data) return { ok: false, error: 'network' };
  return data as CommandResult;
}

/** Plain words for each refusal (25 TON-068: no codes on their own). */
const MESSAGES: Record<string, string> = {
  network: 'That did not reach the server. Check your internet, then try again.',
  not_signed_in_as_admin: 'You are signed out. Sign in again.',
  step_up_required: 'For this, type a new code from your authenticator app first.',
  reason_required: 'Write a short reason first.',
  forbidden: 'Your role cannot do this.',
  own_account: 'You cannot do this to your own account. Ask another admin.',
  confirm_key_mismatch: 'The setting name you typed does not match. Type it exactly.',
  self_approval: 'Another owner must approve a change you asked for.',
  out_of_range: 'That value is outside the allowed range.',
  not_pending: 'This is no longer waiting. Refresh the page.',
  expired: 'This request ran out after 7 days. Ask for it again.',
  last_owner: 'You cannot remove the last owner.',
  use_the_invite_function: 'Use the invite form for this.',
  // Stage 2 (decision record 0071): the guards on a ⚖ change, and the proposal's own checks.
  proof_red:
    'A proof this number feeds is not green. It can be approved once that proof passes with the new value.',
  thought_under_line:
    'With this value one of the twelve thoughts would be picked too seldom. Choose another value.',
  impact_not_ready:
    'The check of the twelve thoughts has not finished for this value. Wait for it, then try again.',
  // Stage 2 (decision record 0216): the sums of the truth's hidden parts (10 SIM-319 test 6).
  h6_sums_share:
    'This share must stay between 0.41 and 0.57, so that the hidden part still hides the truth and stays inside every band. Choose another value.',
  h6_sums_green_wide:
    "With this value the green's day and the spot together would be too big to stay inside a band. Choose a smaller value, or lower the other one first.",
  h6_sums_green_small:
    "With this value the green's day and the spot together would hide too little. Choose a bigger value, or raise the other one first.",
  h6_sums_cell_key:
    "The settings hold a green's cell size of the engine's own. Only the course settings may hold it. Remove that setting first.",
  unknown_key: 'There is no setting with that name.',
  wrong_type: 'That value is the wrong kind for this setting.',
  value_required: 'Type a value first.',
  not_allowed: 'That value is not one this setting allows.',
  fixed_after_launch: 'This setting is fixed now. It cannot change.',
  round_in_play:
    'This round is still being played. A round in play is never opened here. Try again when it has finished.',
  round_not_found: 'No round has that number. Check it, then try again.',
  bad_value: 'That is not a value the server can read. Type a number, true or false, or a list.',
  // Stage 5 (decision record 0308): the live-ops controls of a course in play (07 CRS-038).
  course_not_found: 'That course is not there any more. Refresh the page.',
  not_in_play: 'That course is not in play any more. Refresh the page.',
  job_in_progress:
    'A new version of this course is already being made. Wait for it in the review queue.',
  no_site: 'This course has no site on record, so it cannot be made again here.',
  seed_used: 'That seed was used before for this course. Try again.',
};

/**
 * The refusal code at the head of a database error's message, as the console's read functions raise
 * them ("round_in_play", "forbidden: support cannot …"). Anything else counts as not reaching the server.
 */
export function codeOf(message: string | undefined): string {
  const m = /^([a-z_]+)(?::|$)/.exec((message ?? '').trim());
  return m?.[1] ?? 'network';
}

export function messageFor(r: CommandResult): string {
  if (r.ok) {
    if (r.outcome === 'held_for_freeze' || r.held_for_freeze === true)
      return 'Approved. A live event is on, so it starts when the event ends.';
    if (r.outcome === 'pending_second_approval') return 'Approved. It now needs a second owner.';
    return 'Done.';
  }
  return MESSAGES[r.error ?? ''] ?? 'That did not work. Refresh the page and try again.';
}
